import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { MoveLeft, ShieldOff } from "lucide-react";
import { Button } from "@/components/Button";

export default function AccessDenied() {
    const navigate = useNavigate();

    return (
        <div className="flex items-center justify-center min-h-screen px-4 bg-background relative overflow-hidden">
            {/* Background blobs – matching the project's error page style */}
            <div className="absolute inset-0 pointer-events-none">
                <motion.div
                    animate={{
                        scale: [1, 1.2, 1],
                        rotate: [0, 90, 0],
                        opacity: [0.1, 0.2, 0.1],
                    }}
                    transition={{ duration: 15, repeat: Infinity, ease: "linear" }}
                    className="absolute -top-[10%] -left-[10%] w-[40%] h-[40%] bg-red-500 rounded-full blur-[120px]"
                />
                <motion.div
                    animate={{
                        scale: [1, 1.3, 1],
                        rotate: [0, -90, 0],
                        opacity: [0.1, 0.15, 0.1],
                    }}
                    transition={{ duration: 20, repeat: Infinity, ease: "linear" }}
                    className="absolute -bottom-[10%] -right-[10%] w-[50%] h-[50%] bg-orange-500 rounded-full blur-[150px]"
                />
            </div>

            <div className="max-w-2xl w-full text-center relative z-10">
                <motion.div
                    initial={{ opacity: 0, y: 30 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.8, ease: "easeOut" }}
                >
                    {/* Animated icon + 403 */}
                    <div className="relative inline-block mb-12">
                        <motion.h1
                            className="text-[120px] md:text-[200px] font-black text-red-500/10 dark:text-red-500/20 select-none leading-none tracking-tighter"
                            animate={{
                                scale: [1, 1.05, 1],
                                rotate: [0, 2, -2, 0],
                            }}
                            transition={{ duration: 6, repeat: Infinity, ease: "easeInOut" }}
                        >
                            403
                        </motion.h1>

                        <div className="absolute inset-0 flex flex-col items-center justify-center gap-3">
                            <motion.div
                                initial={{ opacity: 0, scale: 0.5 }}
                                animate={{ opacity: 1, scale: 1 }}
                                transition={{ delay: 0.1, duration: 0.5, type: "spring" }}
                                className="flex items-center justify-center w-16 h-16 bg-red-100 dark:bg-red-900/30 rounded-2xl shadow-lg"
                            >
                                <ShieldOff className="w-8 h-8 text-red-500 dark:text-red-400" />
                            </motion.div>
                            <motion.h2
                                initial={{ opacity: 0, scale: 0.8 }}
                                animate={{ opacity: 1, scale: 1 }}
                                transition={{ delay: 0.2, duration: 0.5 }}
                                className="text-2xl md:text-4xl font-bold text-slate-900 dark:text-white"
                            >
                                Access Denied
                            </motion.h2>
                        </div>
                    </div>

                    <p className="text-slate-600 dark:text-slate-400 mb-12 max-w-md mx-auto leading-relaxed">
                        You don&apos;t have permission to view this page.
                        Please contact your workspace administrator if you believe this is a mistake.
                    </p>

                    <div className="flex flex-col sm:flex-row items-center justify-center gap-6">
                        <Button
                            variant="outline"
                            onClick={() => navigate(-1)}
                            className="w-full sm:w-auto px-8 py-5 flex items-center justify-center group"
                        >
                            <MoveLeft className="mr-2 h-5 w-5 transition-transform group-hover:-translate-x-1" />
                            Go Back
                        </Button>
                    </div>
                </motion.div>
            </div>
        </div>
    );
}
